import React from "react";
import { Link } from "react-router-dom";
import { 
  Droplets, 
  Phone, 
  Mail, 
  ShoppingCart, 
  ChevronRight,
  Shield,
  Award,
  MapPin,
  Factory,
  Waves,
  Palette,
  ArrowLeft,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";

const FILTERING_STAGES = [
  { stage: 1, name: "Sand Filter", desc: "Removes large particles and sediment" },
  { stage: 2, name: "Carbon Filter", desc: "Eliminates chlorine, odors, and organic compounds" },
  { stage: 3, name: "Water Softener", desc: "Reduces mineral hardness for smoother taste" },
  { stage: 4, name: "Micron Filter", desc: "Captures microscopic impurities" },
  { stage: 5, name: "Membrane Filter", desc: "Reverse osmosis for molecular-level purity" },
  { stage: 6, name: "UV System", desc: "Ultraviolet light destroys bacteria and viruses" },
  { stage: 7, name: "Ozone System", desc: "Ozone treatment for final disinfection" },
  { stage: 8, name: "Micron Filter", desc: "Final polish filtration before bottling" },
];

const BOTTLING_PARTNERS = [
  {
    name: "Bleu Flux Beverage Co",
    location: "South Houston, TX",
    desc: "State-of-the-art purification and bottling facility serving the greater Houston metro area."
  },
  {
    name: "Perfect Hydration",
    location: "Missouri City, TX",
    desc: "Premium water processing facility with high-capacity bottling lines for retail and private label production."
  }
];

export default function BlueFluxAboutPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/hospital/marketplace" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Marketplace
          </Link>
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-sky-500" />
            <span className="font-bold text-slate-800">Bleu Flux</span>
            <span className="text-slate-400 text-sm">Water Distribution</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-sky-700 via-blue-800 to-sky-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-5 py-2 rounded-full mb-6">
              <Droplets className="w-5 h-5 text-sky-300" />
              <span className="text-sky-200 font-semibold tracking-wide">Bleu Flux Beverage Co</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-testid="bleuflux-about-title">
              The Evolution of H<sub>2</sub>O
            </h1>
            <p className="text-xl text-sky-200 leading-relaxed mb-8 max-w-2xl mx-auto">
              <em>Bleu Flux</em> — translated "Blue Streams" — delivers premium purified drinking water through an advanced 8-stage filtration process. Locally bottled in South Houston and Missouri City, Texas.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/hospital/blueflux">
                <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 font-bold text-lg px-8">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Order Products
                </Button>
              </Link>
              <a href="tel:2814563458">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8">
                  <Phone className="w-5 h-5 mr-2" />
                  (281) 456-3458
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Statement */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Our Mission</span>
            <h2 className="text-3xl font-bold text-slate-800 mt-2 mb-4">Clean Water. Local Source. Pure Taste.</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our clean water process combines Micron Filtering, Reverse Osmosis, and UV filtering to guarantee a fresh and pure taste every time. Bleu Flux water is sourced locally and delivers a refreshing, thirst-quenching, clean taste.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Beyond water, Bleu Flux is a premiere distributor of food, medical, and industrial supplies offering products to businesses throughout North America. We are committed to providing the highest levels of service and quality products to our customers.
            </p>
            <div className="flex flex-wrap gap-3">
              {["FDA Compliant", "Locally Sourced", "8-Stage Filtration", "Private Label Available"].map((tag) => (
                <span key={tag} className="bg-sky-50 text-sky-700 px-3 py-1.5 rounded-full text-sm font-medium border border-sky-100">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg border">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/1ebe7a101d31a4ce760e81e176eb960aa656078c5336cd0c89674109b3d56b6f.png" 
              alt="Bleu Flux Water Bottling Facility"
              className="w-full h-72 object-cover"
            />
            <div className="p-4 bg-slate-50">
              <p className="text-sm text-slate-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-500" /> Bottled locally in South Houston & Missouri City, Texas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottling Partners */}
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sky-400 font-semibold text-sm uppercase tracking-wider">Our Bottling Partners</span>
            <h2 className="text-3xl font-bold mt-2">Locally Bottled in Texas</h2>
            <p className="text-slate-400 mt-2 max-w-xl mx-auto">
              Proudly partnered with premier bottling facilities in the greater Houston area to ensure the highest quality and freshest product.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {BOTTLING_PARTNERS.map((partner, i) => (
              <div key={i} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-sky-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Factory className="w-7 h-7 text-sky-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{partner.name}</h3>
                    <p className="text-sky-400 flex items-center gap-1 text-sm mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {partner.location}
                    </p>
                    <p className="text-slate-400 mt-2 text-sm leading-relaxed">{partner.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 8-Stage Filtering Process */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Our Process</span>
          <h2 className="text-3xl font-bold text-slate-800 mt-2">8-Stage Purification Process</h2>
          <p className="text-slate-500 mt-2 max-w-xl mx-auto">
            Every drop of Bleu Flux water passes through our rigorous 8-stage filtration system for guaranteed purity and taste.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {FILTERING_STAGES.map((stage) => (
            <div key={stage.stage} className="bg-white rounded-xl border-2 border-slate-100 p-5 hover:border-sky-200 hover:shadow-md transition-all group">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-sky-600 rounded-lg flex items-center justify-center text-white font-bold text-sm group-hover:bg-sky-700 transition-colors">
                  {stage.stage}
                </div>
                <Waves className="w-5 h-5 text-sky-400" />
              </div>
              <h3 className="font-bold text-slate-800 text-sm">{stage.name}</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{stage.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Product Listings */}
      <div className="bg-gradient-to-br from-slate-50 to-sky-50 py-16 border-y">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-sky-600 font-semibold text-sm uppercase tracking-wider">Our Products</span>
            <h2 className="text-3xl font-bold text-slate-800 mt-2">Shop Bleu Flux Water</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Fresh N Pure Product Card */}
            <Link to="/hospital/blueflux" className="block group" data-testid="fresh-n-pure-product-card">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-2xl hover:border-sky-300 transition-all h-full flex flex-col">
                {/* Product Image */}
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-6 flex items-center justify-center relative h-56">
                  <span className="absolute top-4 left-4 bg-sky-600 text-white text-xs font-bold px-3 py-1 rounded-md">72 cases/pallet</span>
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/0acdc59060ccf5fb6153bb71a98997f77fe1d0efc57bcf8288dc7121139334ee.png"
                    alt="Fresh N Pure Purified Water"
                    className="max-h-[200px] object-contain"
                  />
                </div>

                {/* Product Details */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Fresh N Pure Purified Water</h3>
                  <p className="text-sm text-slate-500 mb-3">Case of 24 Individual 500 mL (16.9 oz) Bottles</p>

                  {/* Rating */}
                  <div className="flex text-amber-400 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" /></svg>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-slate-900">$5.99</span>
                    <span className="text-sm text-slate-400">/case</span>
                  </div>

                  {/* Pallet Breakdown */}
                  <div className="bg-sky-50 border border-sky-100 rounded-lg p-3 mb-4 text-sm space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-slate-500">72 cases x $5.99</span>
                      <span className="text-slate-700">$431.28</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Delivery & Handling</span>
                      <span className="text-slate-700">$163.72</span>
                    </div>
                    <div className="flex justify-between border-t border-sky-200 pt-1.5">
                      <span className="font-bold text-slate-800">Total per Pallet</span>
                      <span className="font-bold text-sky-700 text-lg">$595.00</span>
                    </div>
                  </div>

                  {/* Specs */}
                  <div className="space-y-1.5 text-sm mb-4">
                    {[["MOQ", "1 Pallet (72 cases)"], ["Bottles/Case", "24 x 16.9 oz"], ["Origin", "Houston, TX"]].map(([l, v]) => (
                      <div key={l} className="flex gap-3">
                        <span className="text-slate-500 w-24 flex-shrink-0">{l}</span>
                        <span className="text-slate-800 font-medium">{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="mt-auto">
                    <span className="text-emerald-600 font-semibold text-xs block mb-2">In Stock — Buy Now</span>
                    <span className="block w-full bg-sky-600 group-hover:bg-sky-700 text-white text-center font-bold py-3 rounded-full transition-colors">
                      Order Now
                    </span>
                  </div>
                </div>
              </div>
            </Link>

            {/* Private Label Product Card */}
            <Link to="/hospital/blueflux" className="block group" data-testid="private-label-product-card">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-2xl hover:border-amber-300 transition-all h-full flex flex-col">
                {/* Product Image */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center relative h-56">
                  <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-md">Custom Label</span>
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/05f7cb08-c38a-4279-84de-df8cd62808bb/images/3f1b06a37181e922dd925b6a57eab99582b013f566a8632a6e0a3969c22f4a22.png"
                    alt="Private Label Water Bottles Pallet"
                    className="max-h-[200px] object-contain"
                  />
                </div>

                {/* Product Details */}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Premium Custom Branded Water</h3>
                  <p className="text-sm text-slate-500 mb-3">16.9 oz Smooth Cylinder Bottles — Add Your Logo</p>

                  {/* Rating */}
                  <div className="flex text-amber-400 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" /></svg>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-bold text-slate-900">$727.84</span>
                    <span className="text-sm text-slate-400 line-through">$1,036.80</span>
                    <span className="text-red-600 font-bold text-xs">30% Off</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">per pallet</p>

                  {/* Volume Pricing */}
                  <div className="grid grid-cols-4 gap-0 border border-slate-200 rounded-lg overflow-hidden mb-4 text-center text-xs">
                    <div className="bg-slate-50 py-1.5 px-1 border-r border-slate-200 font-medium text-slate-600">1-4</div>
                    <div className="bg-slate-50 py-1.5 px-1 border-r border-slate-200 font-medium text-slate-600">5-10</div>
                    <div className="bg-slate-50 py-1.5 px-1 border-r border-slate-200 font-medium text-slate-600">11-19</div>
                    <div className="bg-slate-50 py-1.5 px-1 font-medium text-slate-600">20+</div>
                    <div className="py-2 px-1 border-r border-t border-slate-200 font-bold text-slate-800">$727.84</div>
                    <div className="py-2 px-1 border-r border-t border-slate-200 font-bold text-slate-800">$687.44</div>
                    <div className="py-2 px-1 border-r border-t border-slate-200 font-bold text-slate-800">$645.84</div>
                    <div className="py-2 px-1 border-t border-slate-200 font-bold text-sky-700">$595.44</div>
                  </div>

                  {/* Specs */}
                  <div className="space-y-1.5 text-sm mb-4">
                    {[["MOQ", "1 Pallet (72 cases)"], ["Yield", "24 Bottles per Case"], ["Type", "Purified Water"]].map(([l, v]) => (
                      <div key={l} className="flex gap-3">
                        <span className="text-slate-500 w-24 flex-shrink-0">{l}</span>
                        <span className="text-slate-800 font-medium">{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* Design Your Label */}
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 mb-4">
                    <span className="text-xs text-slate-600 font-medium">Design Your Label</span>
                    <span className="text-xs text-sky-600 bg-sky-50 border border-sky-200 rounded px-2 py-0.5 font-medium">Add Logo and/or Text</span>
                  </div>

                  {/* CTA */}
                  <div className="mt-auto">
                    <span className="text-emerald-600 font-semibold text-xs block mb-2">In Stock — Buy Now</span>
                    <span className="block w-full bg-sky-600 group-hover:bg-sky-700 text-white text-center font-bold py-3 rounded-full transition-colors">
                      Order Now
                    </span>
                    <p className="text-xs text-slate-400 mt-1.5 text-center">Need a Quote? Quote option available at checkout.</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-sky-600 to-blue-700 rounded-2xl p-10 text-white text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to Get Started?</h2>
          <p className="text-sky-200 mb-8 max-w-lg mx-auto">
            Whether you need branded water, private label, or distribution partnerships — we're here to help.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/hospital/blueflux">
              <Button size="lg" className="bg-white text-sky-700 hover:bg-sky-50 font-bold px-8">
                <ShoppingCart className="w-5 h-5 mr-2" />
                Order Products
              </Button>
            </Link>
            <a href="tel:2814563458">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-8">
                <Phone className="w-5 h-5 mr-2" />
                Call (281) 456-3458
              </Button>
            </a>
            <a href="mailto:orders@sterilemate.com">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-8">
                <Mail className="w-5 h-5 mr-2" />
                orders@sterilemate.com
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-10 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Droplets className="w-6 h-6 text-sky-400" />
              <div>
                <p className="font-bold">Bleu Flux Beverage Co</p>
                <p className="text-slate-400 text-sm">A Sterile Mate Distribution Partner</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="tel:2814563458" className="text-slate-300 hover:text-white transition-colors flex items-center gap-2">
                <Phone className="w-4 h-4" /> (281) 456-3458
              </a>
              <a href="mailto:orders@sterilemate.com" className="text-slate-300 hover:text-white transition-colors flex items-center gap-2">
                <Mail className="w-4 h-4" /> orders@sterilemate.com
              </a>
              <a href="https://bleuflux.com" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors flex items-center gap-2">
                <ExternalLink className="w-4 h-4" /> bleuflux.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
